import React, { useMemo, useEffect } from 'react';
import SectionCard from '../components/SectionCard.jsx';
import KPICard from '../components/KPICard.jsx';
import ContextPanel from '../components/ContextPanel.jsx';
import DataTable from '../components/DataTable.jsx';
import MoneyCell from '../components/MoneyCell.jsx';
import EduTip from '../components/EduTip.jsx';
import { resolveTrendVisual } from '../components/formatAmount.js';
import SettingsDrawer from '../components/SettingsDrawer.jsx';
import DriverPanel from '../components/DriverPanel.jsx';
import InflationPanel from '../components/InflationPanel.jsx';
import SeasonalEditor from '../components/SeasonalEditor.jsx';
import ComparisonView from '../components/ComparisonView.jsx';
import FormalStatementTable, { shapeCashFlowStatement, shapeIncomeStatement, shapeBalanceSheet, shapeIncomeStatementMulti, shapeBalanceSheetMulti, shapeCashFlowStatementMulti } from '../components/FormalStatementTable.jsx';
import FormalStatementSection from '../components/FormalStatementSection.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import ComparativeTablesView from '../components/ComparativeTablesView.jsx';
import ModuleSettingsPanel from '../components/ModuleSettingsPanel.jsx';
import MiniSparkline, { SparklineDeltaBadge } from '../components/MiniSparkline.jsx';
import ModifiedBadge, { isJMRepayOverridden, OVERRIDE_REASON } from '../components/ModifiedBadge.jsx';
import EmptyState from '../components/EmptyState.jsx';
import {
  fmtMoney, fmtPct, currentYM,
  buildThreeStatementProjection,
  calcRunway, calcFVFValue,
  reserveMilestoneProgress,
  totalAssetsLiquid, totalCreditCardDebt, totalCCCredits,
  totalLongTermLiabilities, totalEquity, totalSuspense,
  monthlyBurnBreakdown, monthlyCashInBreakdown,
  fvfMRRBreakdown, seasonalBreakdown, lifestyleBreakdown, fuelBreakdown,
  recurringBreakdown,
  calcMonthlyFuel, calcSeasonalForMonth, calcLifestyleForMonth,
  applyAdjustingEntries,
  buildIncomeStatement,
  totalFixedAssetsNet, totalCCDebtFromBlocks, totalCCCreditsFromBlocks, totalLTLFromBlocks,
  buildThreeStatementForDirection, getTabDirection,
  aggregateByDensity, fmtPeriod, normalizePeriodScalar, getEffectiveHorizon,
  currentPeriodStateForRows,
  getModuleFeatures,
} from '../engine.js';
import {
  TrajectoryChart, CashFlowChart, NetWorthChart, ReserveMilestoneProgress, BurnStackedChart,
  RunwayGauge,
} from '../charts/index.jsx';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const KPI_ROW = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mb-5';
const KPI_ROW_COMPACT = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mt-3 mb-0';
const GRID_2 = 'grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4';
const FORECASTS_SETTINGS_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4 items-start';
const METHOD_CHIP = 'inline-flex items-center gap-[6px] px-2 py-[3px] text-[10px] font-bold tracking-[0.06em] uppercase bg-[rgba(99,102,241,0.15)] text-[#a5b4fc] rounded-[10px] border border-[rgba(99,102,241,0.3)]';
const COMPACT_LIST = 'mt-[0.6rem] mb-0 pl-[1.1rem] text-text-muted text-[0.84rem] leading-[1.65]';

export default function ForecastsTab({ scenario, setScenario, onJump, activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);
  /* v8 — horizon now comes from the global Period × Density picker in
     the ScenarioBar, so every chart and rolling table here resizes with
     the user's global choice. */
  const horizon = getEffectiveHorizon(scenario);

  /* v8 Path E — scroll-to-section when sidebar routes to a sub-item. */
  useEffect(() => {
    if (!activeSection || typeof document === 'undefined') return;
    const t = setTimeout(() => {
      const el = document.getElementById(`section-${activeSection}`) || document.getElementById(activeSection);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.remove('section-focused');
      void el.offsetWidth;
      el.classList.add('section-focused');
      setTimeout(() => el.classList.remove('section-focused'), 1900);
    }, 60);
    return () => clearTimeout(t);
  }, [activeSection]);

  const adj = scenario.includeAdjustments !== false
    ? applyAdjustingEntries(scenario)
    : { byAccount: {}, totals: { is: 0, bs: 0 }, count: 0, entries: [] };
  const isExpenseAdj = adj.count > 0 ? -adj.totals.is : 0;
  const bsAdjEntries = adj.entries.filter(e => e.status === 'posted' && (e.affects ?? []).includes('bs'));

  const { forecast, incomeStatements, balanceSheets, cashFlows } = useMemo(
    () => buildThreeStatementProjection(scenario, horizon),
    [scenario, horizon],
  );

  const forecastsPeriods = normalizePeriodScalar(scenario.formalPeriodCount, 12);
  const forecastsDensity = normalizePeriodScalar(scenario.formalPeriodDensity, 1);
  const forecastsHorizon = forecastsPeriods * forecastsDensity;
  const forecastsDirection = getTabDirection(scenario, 'forecasts');
  const forecastsDirLabel = forecastsDirection === 'past' ? 'trailing' : forecastsDirection === 'future' ? 'leading' : 'spanning';
  const forecastsProjection = useMemo(() => {
    if (forecastsPeriods <= 1) return null;
    const raw = buildThreeStatementForDirection(scenario, forecastsDirection, forecastsHorizon, forecastsDensity);
    const forecast         = aggregateByDensity(raw.forecast,         forecastsDensity, 'last');
    const incomeStatements = aggregateByDensity(raw.incomeStatements, forecastsDensity, 'sum');
    const balanceSheets    = aggregateByDensity(raw.balanceSheets,    forecastsDensity, 'last');
    const cashFlows        = aggregateByDensity(raw.cashFlows,        forecastsDensity, 'sum');
    const periodLabels     = forecast.map(r => fmtPeriod(r.ym ?? r.label, forecastsDensity));
    const currentState     = currentPeriodStateForRows(forecast, forecastsDensity);
    const currentLabel     = currentState.label;
    return { forecast, incomeStatements, balanceSheets, cashFlows, periodLabels, currentLabel, currentState };
  }, [scenario, forecastsPeriods, forecastsDensity, forecastsHorizon, forecastsDirection]);
  const forecastsIS = forecastsProjection ? forecastsProjection.incomeStatements : incomeStatements.slice(0, forecastsPeriods);
  const forecastsBS = forecastsProjection ? forecastsProjection.balanceSheets    : balanceSheets.slice(0, forecastsPeriods);
  const forecastsCF = forecastsProjection ? forecastsProjection.cashFlows        : cashFlows.slice(0, forecastsPeriods);
  const forecastsPeriodLabels = forecastsProjection
    ? forecastsProjection.periodLabels
    : forecast.slice(0, forecastsPeriods).map(r => r.label);
  const forecastsCurrentLabel = forecastsProjection?.currentLabel
    ?? currentPeriodStateForRows(forecast.slice(0, forecastsPeriods), forecastsDensity).label;
  const forecastsCurrentProof = forecastsProjection?.currentState
    ?? currentPeriodStateForRows(forecast.slice(0, forecastsPeriods), forecastsDensity);
  const runway = calcRunway(scenario);
  const dcf = calcFVFValue(scenario);
  const progress = reserveMilestoneProgress();

  const finalRow = forecast[forecast.length - 1];
  const totalFCF = forecast.reduce((s, r) => s + r.netFCF, 0);
  const method = scenario.fvfMethod ?? 'triangulated';
  const transpose = scenario.tableOrientation === 'horizontal';
  const formal = scenario.statementStyle === 'formal';
  const cmpMode = scenario.comparisonMode ?? 'none';

  const moneyColWithBreakdown = (key, breakdownFn, term, registryKey) => ({
    key,
    align: 'right',
    format: (v, row) => (
      <MoneyCell
        value={v}
        breakdown={breakdownFn ? breakdownFn(scenario, row.ym) : undefined}
        term={term}
        registryKey={registryKey}
        onJump={onJump}
        decimals={2}
      />
    ),
  });

  const moneyCol = (key, label) => ({
    key,
    align: 'right',
    format: (v, row) => typeof v === 'number'
      ? <MoneyCell value={v} decimals={2} scenario={scenario} label={label || key} row={row} />
      : v,
  });

  if (activeSection === 'comparative') {
    return (
      <div className={TAB_CONTENT}>
        <SubViewBreadcrumb moduleId="forecasts" activeSection={activeSection} onBack={onBackToOverview} />
        <ComparativeTablesView scenario={scenario} moduleId="forecasts" />
      </div>
    );
  }

  const features = getModuleFeatures(scenario, 'forecasts');

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId="forecasts" activeSection={activeSection} onBack={onBackToOverview} />
      {show('overview') && (
        <ModuleSettingsPanel
          scenario={scenario}
          setScenario={setScenario}
          moduleId="forecasts"
          title="Forecasts · module settings"
          features={[
            { key: 'drivers',          label: 'Universal model drivers',   hint: 'Top-down / bottom-up / triangulated panel' },
            { key: 'overlays',         label: 'Overlays',                  hint: 'Inflation · Fuel · Seasonal · Lifestyle' },
            { key: 'comparison',       label: 'Base · Upside · Downside',  hint: 'Comparison view across model states' },
            { key: 'scenario',         label: 'Forecast controls',         hint: 'Drivers, overlays, and scenario inputs' },
            { key: 'formalStatements', label: 'Formal statements',         hint: 'IS / BS / CF × horizon' },
          ]}
        />
      )}
      {show('overview', 'model-assumptions') && features.scenario && (
      <div id="section-model-assumptions" style={{ scrollMarginTop: 130 }}>
      <SettingsDrawer
        title="Forecast controls — model drivers · macro overlays · seasonality · operations"
        badge={`${method} · CPI ${fmtPct(scenario.cpiAnnualPct ?? 0.04)}`}
        defaultOpen={false}
        forceOpen={activeSection === 'model-assumptions'}
      >
        <div className={FORECASTS_SETTINGS_GRID}>
          <DriverPanel scenario={scenario} setScenario={setScenario} />
          <InflationPanel scenario={scenario} setScenario={setScenario} />
          <SeasonalEditor scenario={scenario} setScenario={setScenario} />
        </div>
      </SettingsDrawer>
      </div>
      )}

      {/* Proposal 8 Tier 2 — every Forecasts KPI carries a forecast chip
          since this module is projection-only. No ledger pedigree. */}
      {show('overview') && (
      <div className={KPI_ROW}>
        <KPICard
          label={<>Cash runway <EduTip term="Runway" /></>}
          value={<RunwayGauge months={runway} max={24} height={90} />}
          sub="red <6 · yellow <12 · green ≥12"
          tone={runway > 12 ? 'good' : runway > 6 ? 'warn' : 'bad'}
          provenance="forecast"
          provenanceTip="Liquid ÷ total monthly expenses across the forecast horizon. Driven by model drivers, overlays, and assumptions; forecast-only, no posted ledger backing."
        />
        <KPICard
          label={<>12mo cumulative FCF <EduTip term="FCF" /></>}
          value={<MoneyCell value={totalFCF} decimals={2} tone={totalFCF > 0 ? 'good' : 'bad'} term="freeCashFlow" registryKey="freeCashFlow" onJump={onJump} label="12mo cumulative Free Cash Flow" />}
          tone={totalFCF > 0 ? 'good' : 'bad'}
          provenance="forecast"
          provenanceTip="Sum of Net FCF across all 12 forecast periods. Driven by gross inflow − burn − overlays."
        />
        <KPICard
          label="Liquid end-of-horizon"
          value={<MoneyCell value={finalRow.liquidEnding} decimals={2} term="liquidAssets" registryKey="liquidAssets" onJump={onJump} label="Liquid end-of-horizon" />}
          sub={`Start: ${fmtMoney(totalAssetsLiquid(), { decimals: 0 })}`}
          provenance="forecast"
          provenanceTip="Projected liquid balance at horizon end. Starts from selected Ledger liquid assets, then accumulates Net FCF month-over-month."
        />
        <KPICard
          label="Short-term debt end-of-horizon"
          value={<MoneyCell value={finalRow.ccDebtEnding} decimals={2} tone={finalRow.ccDebtEnding < totalCreditCardDebt() ? 'good' : 'warn'} term="ccDebt" registryKey="ccDebt" onJump={onJump} label="Short-term debt end-of-horizon" />}
          sub={`Start: ${fmtMoney(totalCreditCardDebt(), { decimals: 0 })}`}
          provenance="forecast"
          provenanceTip="Projected short-term debt at horizon end. Starts from selected Ledger short-term debt, then applies projected paydown in Net FCF positive months."
        />
        <KPICard
          label={<>Subscription DCF <EduTip term="DCF" /></>}
          value={<MoneyCell value={dcf.total} decimals={2} tone="good" term="FVFValue" registryKey="fvfValue" onJump={onJump} label="Recurring revenue DCF (5-year + terminal)" />}
          sub={`PV ${fmtMoney(dcf.pv, { decimals: 0 })} + term. ${fmtMoney(dcf.terminalValue, { decimals: 0 })}`}
          accent="#fbbf24"
          provenance="forecast"
          provenanceTip="5-year discounted cash flow + terminal value. Highly assumption-laden — sensitivity to discount rate, growth rate, and terminal multiple."
        />
        <KPICard
          label={<>Recurring revenue method <EduTip term={method === 'top-down' ? 'Top-down' : method === 'bottom-up' ? 'Bottom-up' : 'Triangulated'} /></>}
          value={<span className={METHOD_CHIP}>{method}</span>}
          sub="Adjust in Forecasts settings"
        />
      </div>
      )}

      {show('overview') && adj.count > 0 && (
        <SectionCard
          title={`Adjusting Entries — ${adj.count} posted`}
          subtitle={`IS expense impact: ${isExpenseAdj >= 0 ? '+' : ''}${fmtMoney(isExpenseAdj, { decimals: 0 })} · BS-affecting entries: ${bsAdjEntries.length}. Base forecast reflects historical burn; JEs represent accruals outside the engine.`}
        >
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Posted entries" value={`${adj.count}`} sub="status: posted" provenance="ledger" provenanceTip="Count of adjusting JEs marked posted in the Command Module. Stack onto every statement that reads adjustingEntries." />
            <KPICard label="IS expense adj." value={fmtMoney(isExpenseAdj, { decimals: 0 })} tone={isExpenseAdj > 0 ? 'warn' : 'good'} sub="accrued but not in burn model" provenance="computed" provenanceTip="Net IS-side movement from posted JEs — accrued expense outside the burn-model engine path." />
            <KPICard label="BS-affecting" value={`${bsAdjEntries.length}`} sub="balance sheet side" provenance="computed" provenanceTip="Count of posted JEs whose `affects` array includes 'bs' — they alter the balance sheet across the horizon." />
          </div>
          <ul className={`${COMPACT_LIST} list-none pl-0`}>
            {adj.entries.filter(e => e.status === 'posted').map(e => (
              <li key={e.id} className="px-[0.5rem] py-[0.4rem] bg-surface-el border border-border rounded-sm mb-[0.3rem] text-[0.85rem]">
                <strong className="text-accent-gold font-mono">{e.date}</strong> — {e.memo || '(no memo)'}
                {' · '}<em className="not-italic text-text-muted text-[0.75rem]">{(e.affects ?? []).join('+').toUpperCase() || '—'}</em>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {show('overview') && (
      <SectionCard
        sectionId="trajectory"
        title="12-month trajectory"
        subtitle="Liquid cash · short-term debt · reserves · recurring revenue"
      >
        <TrajectoryChart data={forecast} />
      </SectionCard>
      )}

      {show('overview', 'overlays') && features.overlays && (
      <div className={GRID_2}>
        <SectionCard sectionId="overlays" title="Monthly cash flow" subtitle="Gross in vs base burn + net FCF overlay">
          <CashFlowChart data={forecast} />
        </SectionCard>
        <SectionCard title="Burn composition (v5 layers)" subtitle="Recurring · Support · Term debt · Fuel · Seasonal · Lifestyle">
          <BurnStackedChart data={forecast} />
        </SectionCard>
      </div>
      )}

      {show('overview') && (
      <SectionCard title="Net worth proxy" subtitle="Liquid + custody - short-term debt - term debt - arrears">
        <NetWorthChart data={forecast} />
      </SectionCard>
      )}

      {show('overview') && (
      <SectionCard
        title="Reserve milestone - savings progress"
        subtitle="Monthly reserve contribution, target 2027-01-01"
      >
        <ReserveMilestoneProgress progress={progress} />
      </SectionCard>
      )}

      {show('overview', 'drivers') && features.drivers && (
      <SectionCard
        sectionId="drivers"
        title="12-month forecast — drill-down every number"
        subtitle="Hover any amount to see what rolls into it. Recurring revenue breakdown follows active method. Sparklines show each driver's 12-month trajectory at a glance — toggle area fill in the model context bar."
      >
        <DriverSparkRow forecast={forecast} gradient={!!scenario?.sparklineGradient} semanticTone={!!scenario?.sparklineSemanticTone} />
        <DataTable
          compact
          columns={[
            { key: 'label', label: 'Month', width: '8%' },
            { ...moneyColWithBreakdown('grossIn', (s, ym) => monthlyCashInBreakdown(s, ym)), label: 'Gross in' },
            { ...moneyColWithBreakdown('fvfMRR', (s, ym) => fvfMRRBreakdown(s, ym), 'MRR', 'fvfMRR'), label: 'Recurring revenue' },
            { ...moneyColWithBreakdown('baseBurn', (s, ym) => monthlyBurnBreakdown(s, ym), null, 'recurringSubs'), label: 'Base burn' },
            { ...moneyColWithBreakdown('fuel', (s, ym) => fuelBreakdown(s, ym), 'Fuel', 'fuel'), label: 'Fuel' },
            { ...moneyColWithBreakdown('seasonal', (s, ym) => seasonalBreakdown(s, ym), null, 'seasonal'), label: 'Seasonal' },
            { ...moneyColWithBreakdown('lifestyle', (s, ym) => lifestyleBreakdown(s, ym), null, 'lifestyle'), label: 'Lifestyle' },
            { ...moneyCol('netFCF'), label: 'Net FCF', className: 't-strong' },
            { ...moneyCol('liquidEnding'), label: 'Liquid EoM' },
          ]}
          rows={forecast.map((r) => ({ ...r, id: r.ym }))}
          transpose={transpose}
        />
      </SectionCard>
      )}

      {show('overview') && (
      <SectionCard
        title="Projected Income Statement (12 months)"
        subtitle="Accrual — includes primary income, recurring revenue ramp, and contribution receivables as other income"
      >
        <DataTable
          compact
          columns={[
            { key: 'label', label: 'Month', width: '8%' },
            { ...moneyCol('salaryGross'), label: 'Primary income gross' },
            { ...moneyColWithBreakdown('fvfRevenue', (s, ym) => fvfMRRBreakdown(s, ym), 'MRR', 'fvfMRR'), label: 'Recurring revenue' },
            { ...moneyColWithBreakdown('familyContribRevenue', null, null, 'educationAR'), label: 'Contribution AR' },
            { ...moneyCol('totalRevenue'), label: 'Total rev', className: 't-strong' },
            { ...moneyColWithBreakdown('subscriptionsExpense', (s, ym) => recurringBreakdown(ym, s), null, 'recurringSubs'), label: 'Subs' },
            { ...moneyColWithBreakdown('familyExpense', null, null, 'familyDirect'), label: 'Direct support' },
            { ...moneyColWithBreakdown('marianExpense', null, null, 'marianAP'), label: 'Partner channel' },
            { ...moneyColWithBreakdown('fuelExpense', (s, ym) => fuelBreakdown(s, ym), 'Fuel', 'fuel'), label: 'Fuel' },
            { ...moneyColWithBreakdown('seasonalExpense', (s, ym) => seasonalBreakdown(s, ym), null, 'seasonal'), label: 'Seasonal' },
            { ...moneyColWithBreakdown('lifestyleExpense', (s, ym) => lifestyleBreakdown(s, ym), null, 'lifestyle'), label: 'Lifestyle' },
            { ...moneyCol('totalOpEx'), label: 'OpEx' },
            { ...moneyCol('mortgageInterest'), label: 'Interest' },
            { ...moneyCol('taxesAndGov'), label: 'Tax + statutory' },
            { ...moneyCol('netIncome'), label: 'Net income', className: 't-strong' },
          ]}
          rows={incomeStatements.map((r) => ({ ...r, id: r.ym }))}
          transpose={transpose}
        />
      </SectionCard>
      )}

      {show('overview') && (
      <SectionCard
        title="Projected Balance Sheet (month-end, 12 months)"
        subtitle="Cash + custody + AR + operating assets vs short-term debt + AP + term debt + equity plug"
      >
        <DataTable
          compact
          columns={[
            { key: 'label', label: 'Month', width: '9%' },
            { ...moneyColWithBreakdown('cash', null, null, 'liquidAssets'), label: 'Cash' },
            { ...moneyCol('custodyReceivable'), label: 'Custody reserve' },
            { ...moneyColWithBreakdown('accountsReceivable', null, null, 'educationAR'), label: 'AR' },
            { ...moneyCol('totalAssets'), label: 'Total assets', className: 't-strong' },
            { ...moneyColWithBreakdown('creditCardsPayable', null, null, 'ccDebt'), label: 'Short-term payable' },
            { ...moneyColWithBreakdown('accountsPayable', null, null, 'marianAP'), label: 'AP + arrears' },
            { ...moneyColWithBreakdown('mortgagesPayable', null, null, 'mortgages'), label: 'Term debt' },
            { ...moneyCol('totalLiabilities'), label: 'Total liab.', className: 't-strong' },
            { ...moneyCol('totalEquity'), label: 'Equity', className: 't-strong' },
          ]}
          rows={balanceSheets.map((r) => ({ ...r, id: r.ym }))}
          transpose={transpose}
        />
      </SectionCard>
      )}

      {show('overview') && (
      <SectionCard
        title={
          <span>
            Projected Cash Flow Statement (indirect, 12 months)
            {isJMRepayOverridden(scenario) && (
              <ModifiedBadge active reason={OVERRIDE_REASON.jmRepay} />
            )}
          </span>
        }
        subtitle={`CFO + CFI + CFF = net cash change${isJMRepayOverridden(scenario) ? ' · ● loan repay schedule customized in Settings' : ''}`}
      >
        <DataTable
          compact
          columns={[
            { key: 'label', label: 'Month', width: '8%' },
            { ...moneyCol('netIncome'), label: 'Net income' },
            { ...moneyCol('deltaAP'), label: 'Δ AP' },
            { ...moneyCol('cfo'), label: 'CFO', className: 't-strong' },
            { ...moneyCol('custodyOut'), label: 'Custody out' },
            { ...moneyCol('cfi'), label: 'CFI', className: 't-strong' },
            { ...moneyCol('ccPaydown'), label: 'Debt paydown' },
            { ...moneyCol('mortgagePrincipal'), label: 'Mortg. principal' },
            { ...moneyCol('jmLumpIn'), label: 'Loan lump in' },
            { ...moneyCol('jmRepayOut'), label: 'Loan repay' },
            { ...moneyCol('cff'), label: 'CFF', className: 't-strong' },
            { ...moneyCol('netCashChange'), label: 'Δ Cash', className: 't-strong' },
          ]}
          rows={cashFlows.map((r) => ({ ...r, id: r.ym }))}
          transpose={transpose}
        />
      </SectionCard>
      )}

      {show('overview', 'comparison') && features.comparison && (
        <SectionCard
          sectionId="comparison"
          title="Period comparison — Base · Upside · Downside"
          subtitle={cmpMode !== 'none'
            ? `Mode: ${cmpMode.toUpperCase()} · Toggle in Settings → Presentation → Period comparison`
            : 'Compare model presets side-by-side (MoM / QoQ / YoY / Hybrid / All-time stats).'}
        >
          {cmpMode !== 'none' ? (
            <ComparisonView forecast={forecast} mode={cmpMode} />
          ) : (
            <EmptyState
              icon="⚖"
              heading="No comparison mode active"
              body="Pick a comparison mode to see Base / Upside / Downside side-by-side with month-on-month, quarter-on-quarter, year-on-year, or all-time statistics."
              actions={[
                { label: 'Open Settings → Presentation', onClick: () => onJump && onJump({ tab: 'settings' }) },
              ]}
            />
          )}
        </SectionCard>
      )}

      {show('overview', 'formal') && features.formalStatements && formal && (
        <SectionCard
          title="Formal Cash Flow Statement — filing style"
          subtitle={`Horizon end (${finalRow.label}) · single amount column · parenthesized negatives`}
        >
          <FormalStatementTable
            entity="Current Lens Entity"
            title="Statement of Cash Flows — Indirect Method"
            period={`For the month ended ${finalRow.label}`}
            sections={shapeCashFlowStatement(cashFlows[cashFlows.length - 1])}
          />
        </SectionCard>
      )}

      {show('overview', 'formal') && features.formalStatements && (<>
      <FormalStatementSection
        tabId="forecasts" stmtKey="cf"
        label="Cash Flow Statement (comparative)"
        subtitle={forecastsPeriods > 1
          ? `${forecastsPeriods}-period ${forecastsDirLabel} · CFO + CFI + CFF`
          : `CFO + CFI + CFF = Net change in cash · ${forecast[0]?.label ?? currentYM()}`}
        title="Statement of Cash Flows — Indirect Method"
        entity="Current Lens Entity"
        period={forecastsPeriods > 1
          ? `${forecastsPeriodLabels[0]} – ${forecastsPeriodLabels[forecastsPeriodLabels.length - 1]}`
          : `For the month ended ${forecast[0]?.label ?? currentYM()}`}
        periods={forecastsPeriods > 1 ? forecastsPeriodLabels : undefined}
        sections={forecastsPeriods > 1
          ? shapeCashFlowStatementMulti(forecastsCF)
          : shapeCashFlowStatement(cashFlows[0] ?? {})}
        currentPeriodLabel={forecastsCurrentLabel}
        currentPeriodProof={forecastsCurrentProof}
        scenario={scenario} setScenario={setScenario}
      />
      <FormalStatementSection
        tabId="forecasts" stmtKey="is"
        label="Income Statement (comparative)"
        subtitle={forecastsPeriods > 1
          ? `${forecastsPeriods}-period ${forecastsDirLabel} IS`
          : `Net income (snapshot): ${fmtMoney(buildIncomeStatement(scenario).netIncome, { decimals: 0 })}`}
        title="Statement of Income — Accrual Basis"
        entity="Current Lens Entity"
        period={forecastsPeriods > 1
          ? `${forecastsPeriodLabels[0]} – ${forecastsPeriodLabels[forecastsPeriodLabels.length - 1]}`
          : `For the month ending ${currentYM()}-30`}
        periods={forecastsPeriods > 1 ? forecastsPeriodLabels : undefined}
        sections={forecastsPeriods > 1
          ? shapeIncomeStatementMulti(forecastsIS)
          : shapeIncomeStatement(buildIncomeStatement(scenario))}
        currentPeriodLabel={forecastsCurrentLabel}
        currentPeriodProof={forecastsCurrentProof}
        scenario={scenario} setScenario={setScenario}
      />
      <FormalStatementSection
        tabId="forecasts" stmtKey="bs"
        label="Balance Sheet (comparative)"
        subtitle={forecastsPeriods > 1
          ? `${forecastsPeriods}-period ${forecastsDirLabel} BS (A = L + E)`
          : `A = L + E · Assets ${fmtMoney(totalAssetsLiquid() + totalFixedAssetsNet(), { decimals: 0 })} · Liab ${fmtMoney(totalCCDebtFromBlocks() - totalCCCreditsFromBlocks() + totalLTLFromBlocks(), { decimals: 0 })}`}
        title="Statement of Financial Position"
        entity="Current Lens Entity"
        period={forecastsPeriods > 1
          ? `${forecastsPeriodLabels[0]} – ${forecastsPeriodLabels[forecastsPeriodLabels.length - 1]}`
          : `As of ${currentYM()}-30`}
        periods={forecastsPeriods > 1 ? forecastsPeriodLabels : undefined}
        sections={forecastsPeriods > 1
          ? shapeBalanceSheetMulti(forecastsBS)
          : shapeBalanceSheet({
              assetsLiquid:    totalAssetsLiquid(),
              fixedAssetsNet:  totalFixedAssetsNet(),
              ccDebt:          totalCCDebtFromBlocks(),
              ccCredits:       totalCCCreditsFromBlocks(),
              ltl:             totalLTLFromBlocks(),
              eq:              totalEquity(),
              susTotal:        totalSuspense(),
            })}
        currentPeriodLabel={forecastsCurrentLabel}
        currentPeriodProof={forecastsCurrentProof}
        scenario={scenario} setScenario={setScenario}
      />
      </>)}

      {/* Proposal 1 (2026-04-25) — module data inheritance. Forecasts
          sees BOTH Budgets (immediate baseline — projection starts from
          present) and Actuals (historical trend — anchors the curve).
          Order matters: closest causal neighbour (Budgets) first. */}
      {show('overview') && (
        <>
          <ContextPanel currentModule="forecasts" contextModule="budgets" scenario={scenario} />
          <ContextPanel currentModule="forecasts" contextModule="actuals" scenario={scenario} />
        </>
      )}
    </div>
  );
}

/* v8 Path K — DriverSparkRow
   Compact 8-metric sparkline grid above the forecast drill-down table.
   Gives an at-a-glance "shape of the forecast" before you dive into
   numbers. Reuses MiniSparkline + wired to the ScenarioBar gradient
   toggle. Each card shows: label · ▲/▼ delta · small sparkline. */
const DRIVER_SPARKS = [
  { key: 'grossIn',      label: 'Gross in',   tone: 'good' },
  /* Each driver declares its favorability via betterWhen. Sparkline tone
     is computed at render time via resolveTrendVisual so it honors the
     Semantic Tone toggle uniformly with the rest of the app:
       - tone OFF: rising = green, falling = red (pure directional)
       - tone ON:  respects betterWhen polarity per metric
     No more hardcoded `tone` that overrides the user's toggle. */
  { key: 'fvfMRR',       label: 'Recurring revenue', betterWhen: 'higher' },
  { key: 'baseBurn',     label: 'Base burn',  betterWhen: 'lower'  },
  { key: 'fuel',         label: 'Fuel',       betterWhen: 'lower'  },
  { key: 'seasonal',     label: 'Seasonal',   betterWhen: 'neutral' },
  { key: 'lifestyle',    label: 'Lifestyle',  betterWhen: 'lower'  },
  { key: 'netFCF',       label: 'Net FCF',    betterWhen: 'higher' },
  { key: 'liquidEnding', label: 'Liquid EoM', betterWhen: 'higher' },
];

function DriverSparkRow({ forecast, gradient, semanticTone }) {
  if (!Array.isArray(forecast) || forecast.length < 2) return null;
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2 mb-3">
      {DRIVER_SPARKS.map(({ key, label, betterWhen }) => {
        const series = forecast.map(r => Number(r[key]) || 0);
        /* Compute tone via the same helper every other sparkline uses.
           Honors semanticTone exactly — no more static override. */
        const { tone: resolvedTone, series: plotSeries } = resolveTrendVisual(
          series, 'currency', betterWhen, !!semanticTone,
        );
        return (
          <div
            key={key}
            className="flex flex-col gap-[2px] px-[0.55rem] py-[0.4rem] bg-surface-el border border-border rounded-sm"
            title={`${label} — 12-month forecast trend`}
          >
            <div className="flex items-baseline justify-between gap-1">
              <span className="text-[0.72rem] text-text-muted truncate">{label}</span>
              <SparklineDeltaBadge series={plotSeries} />
            </div>
            <MiniSparkline
              series={plotSeries}
              tone={resolvedTone}
              size="md"
              gradient={gradient}
              ariaLabel={`${label} forecast sparkline`}
              className="w-full"
            />
          </div>
        );
      })}
    </div>
  );
}
